<template>
  <v-container style="max-width: 650px;">
    <PageHeader />

    <v-card :loading="loading">
      <v-card-title>
        <div class="d-flex align-center">
          Security
          <v-img
            class="mx-5"
            contain
            src="@/assets/settings/security.png"
            max-width="45"
            max-height="45"
          />
        </div>
      </v-card-title>

      <v-card-text class="px-0">
        <v-skeleton-loader
          v-if="!securityInfo || loading"
          class="mx-auto"
          height="450"
          type="image, card"
        />

        <template v-else>
          <v-divider />
          <!-- Section - Security activity -->
          <section class="section">
            <v-row align="center">
              <v-col
                cols="12"
                sm="6"
              >
                <span class="primary--text font-weight-bold">
                  Recent security activity
                </span>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                class="text-right"
              >
                <div>
                  Last log in:
                  {{ securityInfo && securityInfo.last_login ? securityInfo.last_login : '-' }}
                </div>
                <div>
                  <v-btn
                    style="text-transform: none;"
                    color="#8d8d8d"
                    outlined
                    rounded
                    x-small
                    :loading="processLogout"
                    @click="logoutEverywhere"
                  >
                    Log out of all devices
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </section>
          <v-divider />

          <!-- Section - Password -->
          <section class="section">
            <v-row align="center">
              <v-col cols="12">
                <span class="primary--text font-weight-bold">
                  Password
                </span>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                Last changed:
                {{ securityInfo && securityInfo.pw_update_at ? securityInfo.pw_update_at : '-' }}
              </v-col>
              <v-col
                cols="12"
                sm="6"
                :class="$vuetify.breakpoint.smAndUp ? 'text-right' : null"
              >
                <v-btn
                  class="mb-2"
                  color="primary lighten-2"
                  outlined
                  rounded
                  :block="!$vuetify.breakpoint.smAndUp"
                  @click="passwordDialog = true"
                >
                  Change Password
                </v-btn>
              </v-col>
            </v-row>
          </section>
          <v-divider />

          <section
            v-if="emailData"
            class="section"
          >
            <v-row align="center">
              <v-col
                cols="12"
                class="d-flex justify-space-between align-center"
              >
                <span class="primary--text font-weight-bold">
                  Email Addresses
                </span>

                <v-btn
                  rounded
                  outlined
                  color="primary lighten-2"
                  @click="emailDialog = true"
                >
                  Edit
                </v-btn>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="emailData.primary_email"
                  label="Primary email"
                  readonly
                />
              </v-col>
            </v-row>

            <v-row align="center">
              <template
                v-if="emailData && emailData.alternative_emails
                  && emailData.alternative_emails.length"
              >
                <v-col
                  v-for="(email, key) in emailData.alternative_emails"
                  :key="`Alt_${key}`"
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    :value="email"
                    :label="`Email ${key + 2}`"
                    readonly
                    disabled
                  />
                </v-col>
              </template>
            </v-row>
          </section>
        </template>
      </v-card-text>
    </v-card>

    <ChangePassword
      :dialog.sync="passwordDialog"
    />
    <EmailEditor
      v-if="securityInfo"
      :dialog.sync="emailDialog"
      :email-data.sync="emailData"
    />
  </v-container>
</template>

<script>
import { mapMutations } from 'vuex';
import accountApi from '@/api/account/account';
import ChangePassword from '../widget/ChangePassword.vue';
import EmailEditor from '../widget/EmailEditor.vue';
import PageHeader from '../widget/PageHeader.vue';

export default {
  name: 'Security',

  components: {
    ChangePassword,
    EmailEditor,
    PageHeader,
  },

  data: () => ({
    loadingList: [],
    processLogout: false,
    securityInfo: null,
    emailData: null,
    passwordDialog: false,
    emailDialog: false,
  }),

  computed: {
    loading() {
      let flag = false;
      if (this.loadingList && this.loadingList.length > 0) {
        flag = true;
      }
      return flag;
    },
  },

  created() {
    this.getSecurity();
    this.getEmails();
  },

  methods: {
    ...mapMutations('account', [
      'logoutUser',
    ]),

    async getSecurity() {
      try {
        this.loadingList.push('run');
        this.securityInfo = await accountApi.getSecurity();
      } catch (err) {
        console.log(err);
        if (err.msg) { this.$toast.error(err.msg); }
      } finally {
        this.loadingList.pop();
      }
    },

    async getEmails() {
      try {
        this.loadingList.push('run');
        this.emailData = await accountApi.getSecurityEmail();
      } catch (err) {
        console.log(err);
        if (err.msg) { this.$toast.error(err.msg); }
      } finally {
        this.loadingList.pop();
      }
    },

    async logoutEverywhere() {
      const confirm = await this.$confirm('Your account will be logged out of all devices. Do you wish to continue?');
      if (confirm) {
        try {
          this.processLogout = true;
          await accountApi.postSecurity();
          this.logoutUser();
          this.$router.push({ name: 'login' });
        } catch (err) {
          console.log(err);
          if (err.msg) {
            this.$toast.error(err.msg);
          }
        } finally {
          this.processLogout = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .section {
    padding: 20px 16px;
  }
</style>
